import posthog from "posthog-js";

/**
 * Typed analytics events (CM-2).
 *
 * One place that owns every custom PostHog event name + its property shape, so
 * event names stay consistent across the app and typos become type errors
 * rather than silently-diverging funnels. Add a new event by extending
 * `AnalyticsEvents`; `capture()` then enforces the correct props at every call
 * site.
 *
 * Only the core conversion funnel lives here — the frictionless free funnel
 * (job_alert_*) and the pitch popup (portfolio_pitch_*) predate this module and
 * capture inline; they can be folded in later without changing behaviour.
 */
export interface AnalyticsEvents {
  /** Candidate clicked through to an official apply URL on a job page. */
  apply_click: {
    job_id: number;
    org_key: string;
    /** Which apply button — the layout differs by breakpoint. */
    placement: "sidebar" | "mobile";
  };
  /** Visitor clicked a "start / create your portfolio" CTA on the landing page. */
  portfolio_cta_click: {
    /** Which landing CTA fired it — for funnel segmentation. `portfolio_page_*`
     * values are the dedicated /portfolio product page (CM-4); the others are
     * the homepage. */
    location:
      | "hero"
      | "product"
      | "final_cta"
      | "portfolio_page_hero"
      | "portfolio_page_plans_free"
      | "portfolio_page_plans_premium"
      | "portfolio_page_plans_membership"
      | "portfolio_page_final";
  };
  /**
   * The paid offer was RENDERED, priced, with nothing clicked (CM-439). Every
   * other premium event on this board fires on a click — `video_app_pitch_opened`
   * counts people who opened the sidebar dialog, `premium_upsell_clicked` people
   * who hit the search-tab wall — so until this existed there was no way to tell
   * "never saw the offer" from "saw it and said no". 7 of 1,281 visitors reached
   * one of those clicks in the week of 2026-09-22. Same event name and `surface`
   * as Finseats, deliberately: one query spans both boards.
   */
  premium_pitch_view: { surface: string };
  /**
   * CM-440 parity sweep of finseats T-306: a signed-out visitor arriving
   * at the post-apply video-application wizard saw the no-account example
   * preview instead of being bounced straight to /auth/login.
   */
  no_account_preview_view: { surface: string };
  /** The preview's "Sign in free to draft yours" CTA was clicked. */
  no_account_preview_cta_click: { surface: string };
  /**
   * The post-apply offer's own CTA — "create a video application" for the job
   * just applied to. Kept apart from portfolio_cta_click because it sells a
   * different thing; mixing them would hide whether this placement worked.
   */
  video_app_cta_click: { job_id: number };
  /** A candidate published their portfolio from the dashboard. */
  portfolio_published: Record<string, never>;
  /** An employer submitted a vacancy on /post-a-job. */
  employer_job_submitted: {
    org_type: string;
    contract_type: string;
  };
  /** The employer began filling the vacancy form (first field touched). Same name as Finseats (CM-475). */
  listing_form_start: Record<string, never>;
  /**
   * Portfolio-spine checkout step, fired via `captureBeforeRedirect` just before
   * the Stripe redirect. `listing_checkout_started` keeps firing beside it —
   * renaming a live event splits the series. Same name/props as Finseats (CM-475).
   */
  checkout_start: { kind: "posting" | "premium"; surface?: string };
  /** Free-text answer to the one-question micro-survey (CM-140). */
  micro_survey_answered: {
    /** Stable question id — answers stay comparable when the copy changes. */
    survey_id: string;
    answer: string;
    /** CM-277: which affordance produced this answer — a one-tap quickOption
     * button vs. the typed textarea — so response-rate analysis can tell the
     * two paths apart instead of lumping them together. */
    method: "quick_option" | "free_text";
  };
  /** The micro-survey card actually rendered — pairs with micro_survey_answered
   * to compute a real response rate instead of guessing whether 0 answers means
   * 0 impressions or 0% completion. */
  micro_survey_shown: {
    survey_id: string;
  };
  /* The board's account-free alert nudge — shown → submit → captured is the
     whole re-engagement funnel, so all four steps are typed. `filters` is the
     search it fired on, which is what makes the funnel segmentable by search. */
  /* `surface` is set only by PostApplyAlert (CM-471), so the post-apply slot
     can be told apart from the board nudge that shares these names. */
  alert_nudge_shown: { filters: string; surface?: "job_post_apply" };
  alert_nudge_submit: { filters: string; surface?: "job_post_apply" };
  alert_nudge_captured: {
    /** "check_inbox" = confirm mail sent; "already_on" = alert existed. */
    state: string;
    filters: string;
    surface?: "job_post_apply";
  };
  alert_nudge_dismissed: { filters: string };
  /** Signed-out visitor bookmarked a job to this device (no account) — CM-169. */
  job_saved_local: { job_id: number };
  /**
   * Visitor used the share button on a job page — which channel they picked.
   * Predates the portfolio-wide share_open/share_click pair and is kept firing
   * on the job surface only, because the Control Center funnel board reads it
   * for this product. New surfaces use share_click alone.
   */
  job_shared: { job_id: number; channel: "linkedin" | "x" | "copy" };
  /** Share menu / native sheet opened — the denominator for share_click. */
  share_open: { surface: string };
  /** A share actually happened, and through which channel. */
  share_click: { surface: string; channel: string };
  /** A visit that arrived from one of our own tagged share links. */
  share_arrival: { surface: string; channel: string };
  /** "Represent this org?" banner on an org page clicked through to /post-a-job (CM-246-adjacent). */
  org_claim_cta_click: { org_key: string };
  /** CM-281: account creation finished — inferred client-side (Supabase user.created_at
   * is fresh) rather than captured from server auth code, so the completion funnel can
   * finally be joined to acquisition source/campaign inside PostHog. */
  sign_up_completed: Record<string, never>;
  /** CM-293: the "your page isn't live yet" nudge shown on non-profile dashboard
   * tabs — shown/clicked pairs give a real response rate instead of inferring
   * effectiveness from the publish rate alone (which has too many other causes). */
  dashboard_publish_nudge_shown: { tab: string; is_paid: boolean };
  dashboard_publish_nudge_clicked: { tab: string; is_paid: boolean };
  /** A candidate saved their name/title/bio/photo/socials — the first editor
   * step, before any project exists. Added because the editor had zero
   * instrumentation between sign-up and portfolio_published, so a run could
   * observe a paid user never publishing but not see how far into the editor
   * they actually got. */
  profile_basics_saved: { has_bio: boolean; has_photo: boolean };
  /** A candidate added or edited a project card. `is_new` splits first-project
   * (the real activation step — an empty portfolio has nothing worth
   * publishing) from later edits. Same blind-spot rationale as
   * profile_basics_saved above. */
  project_saved: { is_new: boolean };
  /**
   * The board search/filter combination returned zero listings. Real-user
   * exit-feedback keeps naming "Couldn't find what I wanted" with no further
   * detail, and every prior run has had to manually re-type one quoted search
   * to check it by hand. This turns that into a queryable coverage-gap signal:
   * group by `query`/`filters` to see which asks are a genuine content gap
   * (always zero) vs. a one-off (a term nobody else searches).
   */
  jobs_zero_results: { query: string; filters: string };
  /**
   * Any board search with non-empty free text, zero-result or not — fired
   * once per rendered search so "relevance-first search results" (a bet that
   * sat 71+ days in test with zero readings, because `$current_url`'s query
   * string is deliberately scrubbed before capture — see
   * `src/lib/analytics-scrub.ts`) finally has a query-text + result-count
   * series to join against `apply_click` within the same PostHog session.
   */
  jobs_search_performed: { query: string; filters: string; result_count: number };
  /**
   * A plain (non-CTA) link to /post-a-job was clicked — header nav, footer,
   * the jobs-board employer banner, alternatives pages, suggest-a-role.
   * Added because employer_cta_clicked + org_claim_cta_click together only
   * explained 19 of 46 /post-a-job pageviews over 30 days; these links were
   * the untracked rest.
   */
  post_a_job_link_click: {
    placement:
      | "header"
      | "footer"
      | "jobs_page"
      | "alternatives_index"
      | "alternatives_detail"
      | "suggest_a_role";
  };
}

/**
 * Capture a typed event. No-ops until PostHog is loaded (it only loads after
 * consent — see PostHogProvider), so calling this before consent is safe and
 * simply drops the event, exactly like the inline captures elsewhere.
 */
export function capture<K extends keyof AnalyticsEvents>(
  event: K,
  ...props: AnalyticsEvents[K] extends Record<string, never>
    ? []
    : [props: AnalyticsEvents[K]]
): void {
  if (!posthog.__loaded) return;
  posthog.capture(event, props[0]);
}

/**
 * Capture, then navigate away. A plain capture() followed by
 * `window.location.href = …` races the batch flush and the step is lost;
 * sendBeacon hands delivery to the browser so the redirect never waits on it.
 * Ported from Finseats (CM-475).
 */
export function captureBeforeRedirect<K extends keyof AnalyticsEvents>(
  event: K,
  props: AnalyticsEvents[K],
  redirect: () => void,
): void {
  if (posthog.__loaded) {
    posthog.capture(event, props, { send_instantly: true, transport: "sendBeacon" });
  }
  redirect();
}

/**
 * The browser's PostHog id, carried into Stripe metadata as
 * `posthog_distinct_id` so the webhook's server-side `purchase` lands on the
 * same person. Empty when PostHog never loaded (no consent) — the caller then
 * stores nothing and the webhook falls back to the user id.
 */
export function currentDistinctId(): string {
  if (!posthog.__loaded) return "";
  return posthog.get_distinct_id() ?? "";
}

/** Person properties attached when a signed-in user is identified. */
export interface IdentifyProps {
  email?: string | null;
  /** Supabase `created_at` — when the account was created. */
  signup_date?: string;
  /** @concept.zone accounts are executor/operator test logins, not real users —
   * flag them so funnel/engagement metrics can filter them out. */
  is_internal_qa?: boolean;
  /** Puts the person in the project's "Internal / Test users" cohort — see internal-user.ts. */
  $internal_or_test_user?: true;
}

/**
 * Attach subsequent events to a signed-in account (person profile). Called when
 * the Supabase session is established/restored — i.e. right after the auth
 * callback lands the user back in the app. With `person_profiles:
 * "identified_only"`, nothing is created for anonymous visitors; identify() with
 * the same distinct id is an idempotent merge, so repeat calls are harmless.
 * No-ops until PostHog is loaded (consent-gated).
 */
export function identify(distinctId: string, props?: IdentifyProps): void {
  if (!posthog.__loaded) return;
  if (posthog.get_distinct_id() === distinctId) return; // already identified
  posthog.identify(distinctId, props);
}
